import React, { useState, useEffect } from 'react';
import { Trash2, Star, Plus, X, MessageSquare, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import ColorSwatchPicker from './ColorSwatchPicker';

interface Review {
  id: string;
  name: string;
  rating: number;
  comment: string;
}

interface ReviewBlockData {
  reviews: Review[];
  accentColor?: string;
}

const DEFAULT_ACCENT_COLOR = '#f97316';

interface ReviewBlockProps {
  data: ReviewBlockData;
  onChange: (data: ReviewBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
}

const ReviewBlock: React.FC<ReviewBlockProps> = ({ data, onChange, onDelete, isPreview }) => {
  const addReview = () => {
    const newReview: Review = {
      id: Date.now().toString(),
      name: '',
      rating: 5,
      comment: ''
    };
    onChange({ ...data, reviews: [...data.reviews, newReview] });
  };

  const updateReview = (id: string, field: keyof Review, value: any) => {
    const updatedReviews = data.reviews.map(review =>
      review.id === id ? { ...review, [field]: value } : review
    );
    onChange({ ...data, reviews: updatedReviews });
  };

  const deleteReview = (id: string) => {
    onChange({ ...data, reviews: data.reviews.filter(review => review.id !== id) });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto slide every 5 seconds
  useEffect(() => {
    if (!isPreview || data.reviews.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % data.reviews.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPreview, data.reviews.length]);

  const accentColor = data.accentColor || DEFAULT_ACCENT_COLOR;

  if (isPreview) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center gap-3 mb-6">
          <MessageSquare className="w-8 h-8" style={{ color: accentColor }} />
          <h3 className="text-2xl font-bold text-gray-900">รีวิวจากลูกค้า</h3>
        </div>
        
        {/* Auto Slider */}
        <div className="relative max-w-4xl mx-auto">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {data.reviews.map((review) => (
                <div key={review.id} className="w-full flex-shrink-0 px-4">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl" style={{ backgroundColor: accentColor }}>
                        {review.name.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-lg">{review.name}</p>
                        {renderStars(review.rating)}
                      </div>
                    </div>
                    <p className="text-gray-700 italic text-lg">"{review.comment}"</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          {data.reviews.length > 1 && (
            <>
              <button
                onClick={() => setCurrentSlide((prev) => (prev - 1 + data.reviews.length) % data.reviews.length)}
                className="absolute left-0 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 rounded-full p-3 shadow-lg transition-all"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>
              <button
                onClick={() => setCurrentSlide((prev) => (prev + 1) % data.reviews.length)}
                className="absolute right-0 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 rounded-full p-3 shadow-lg transition-all"
              >
                <ChevronRight className="w-6 h-6 text-gray-700" />
              </button>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-6">
                {data.reviews.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-3 rounded-full transition-all ${index === currentSlide ? 'w-8' : 'w-3 bg-gray-300'}`}
                    style={index === currentSlide ? { backgroundColor: accentColor } : undefined}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">รีวิวลูกค้า</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">แสดงความคิดเห็นจากลูกค้า</p>
              <p className="text-gray-300">เพิ่มความน่าเชื่อถือด้วยรีวิวจากลูกค้าจริง พร้อมคะแนนดาวและรูปภาพ เพื่อสร้างความมั่นใจให้ลูกค้าใหม่</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ColorSwatchPicker
            compact
            color={accentColor}
            fallback={DEFAULT_ACCENT_COLOR}
            onChange={(hex) => onChange({ ...data, accentColor: hex })}
          />
          <span className="text-sm font-medium text-gray-700">สีอวาตาร์/ตัวเลื่อน</span>
        </div>

        {data.reviews.map((review, index) => (
          <div key={review.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-start mb-3">
              <span className="text-sm font-medium text-gray-700">รีวิว #{index + 1}</span>
              <button
                onClick={() => deleteReview(review.id)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อลูกค้า</label>
                <input
                  type="text"
                  value={review.name}
                  onChange={(e) => updateReview(review.id, 'name', e.target.value)}
                  placeholder="คุณสมชาย"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">คะแนน</label>
                <select
                  value={review.rating}
                  onChange={(e) => updateReview(review.id, 'rating', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                >
                  <option value={5}>⭐⭐⭐⭐⭐ (5 ดาว)</option>
                  <option value={4}>⭐⭐⭐⭐ (4 ดาว)</option>
                  <option value={3}>⭐⭐⭐ (3 ดาว)</option>
                  <option value={2}>⭐⭐ (2 ดาว)</option>
                  <option value={1}>⭐ (1 ดาว)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ความคิดเห็น</label>
                <textarea
                  value={review.comment}
                  onChange={(e) => updateReview(review.id, 'comment', e.target.value)}
                  placeholder="สินค้าดีมาก ประทับใจ..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={addReview}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          เพิ่มรีวิว
        </button>
      </div>
    </div>
  );
};

export default ReviewBlock;
